import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, LinkModel } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { trackAsImpersonation, trackGaToolbarNavClickEvent } from "@stoneage/App/Utilities/DataLayer";
import Tooltip, { TooltipPresentationProps } from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import iconsObject from "@stoneage/XDL/Icons/AllIcons";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    links = "links",
    l2Links = "l2Links",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
    };
    extendedStyles?: HeaderLinkListStyles;
}

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const pageType = getCurrentPage(state).type;

    return {
        isSticky: state.xHeader.isSticky,
        isHomePage: pageType === "HomePage", // HomePage
        headerLinkListLinks: state.xHeader.headerLinkListLinks,
        toggleHeaderColor: state.xHeader.toggleHeaderColor,
        impersonation: trackAsImpersonation(state),
    };
};

const mapDispatchToProps = {};

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            padding-right: 30px;
            margin-right: 30px;
            border-right: 1px solid ${getColor("common.ui5")};

            .header_links-popup {
                [class*="TooltipTriggerer"][aria-expanded="true"] {
                    [class*="IconWrapper"] {
                        transform: rotate(-180deg);
                        transition: 0.2s;
                    }
                }
            }
        `,
    },
    linkWrapper: {
        css: css`
            display: flex;
            align-items: center;
            margin-right: 30px;

            > a span {
                display: flex;
                align-items: center;
            }

            &:last-child {
                margin-right: 0;
            }

            &,
            span {
                font-size: 12px;
                line-height: 13px;
                color: ${getColor("common.white")} !important;
                font-weight: 500 !important;
                display: flex;
                align-items: center;
            }
            &.toggle {
                &,
                span {
                    color: ${getColor("secondary")} !important;
                }

                svg {
                    color: ${getColor("common.ui6")} !important;
                }

                &:hover,
                &:focus {
                    &,
                    span,
                    svg {
                        color: ${getColor("primary")} !important;
                    }
                }
            }

            .link-icon {
                margin-bottom: 2px;
                &,
                svg {
                    height: 12px;
                    width: 12px;
                }
                svg {
                    color: ${getColor("common.white")};
                }
                &.toggle svg {
                    color: ${getColor("text.main")};
                }
            }

            .prev {
                width: 16px;
                height: 16px;
                margin-right: 5px;
                margin-bottom: 3px;

                &.toggle svg {
                    color: ${getColor("text.main")};
                }
            }
        `,
    },
};

const defaultCustomCss = `.list-wrapper {
}

.list-link {
}
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const HeaderLinkList: React.FC<Props> = ({
    id,
    fields,
    extendedStyles,
    isSticky,
    isHomePage,
    headerLinkListLinks,
    toggleHeaderColor,
    impersonation,
}) => {
    const styles = useMergeStyles("headerLinkList", headerLinkListStyles, extendedStyles);

    const [tippyElement, setTippyElement] = React.useState(undefined);

    if (headerLinkListLinks.length < 1) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const closeTooltip = () => {
        (tippyElement as any)?.hide();
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper
                className="list-wrapper"
                {...styles.headerLinkListWrapper}
                {...({ isSticky, isHomePage, toggleHeaderColor } as any)}
            >
                {headerLinkListLinks.map(
                    // eslint-disable-next-line array-callback-return
                    (linkItem: any, index: any) => {
                        const image = (fields.links[index].fields as any).icon;
                        let menuHasAtLeastOneNavigationalItem = false;
                        if ((fields.links[index].fields as any).isMenu && linkItem?.children?.length) {
                            menuHasAtLeastOneNavigationalItem = linkItem.children!.some(
                                (child: { excludeFromNavigation: any }) => !child.excludeFromNavigation,
                            );
                        }
                        if (
                            linkItem.link.url &&
                            !linkItem.excludeFromNavigation &&
                            (!(fields.links[index].fields as any).isMenu ||
                                ((fields.links[index].fields as any).isMenu && !menuHasAtLeastOneNavigationalItem))
                        ) {
                            return (
                                <StyledWrapper
                                    {...styles.linkWrapper}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`link${linkItem.link.url}.${linkItem.link.title}${index}`}
                                    {...({ isSticky, isHomePage, toggleHeaderColor } as any)}
                                    className={isSticky || !isHomePage || toggleHeaderColor ? "toggle" : ""}
                                >
                                    <Link
                                        href={linkItem.link.url}
                                        target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                        onClick={() => {
                                            trackGaToolbarNavClickEvent(
                                                fields.links[index].fields.overriddenTitle || linkItem.link.title,
                                                impersonation,
                                            );
                                        }}
                                    >
                                        <Icon
                                            src={image}
                                            className={
                                                isSticky || !isHomePage || toggleHeaderColor
                                                    ? "link-icon prev toggle"
                                                    : "link-icon prev"
                                            }
                                        />
                                        {fields.links[index].fields.overriddenTitle || linkItem.link.title}
                                    </Link>
                                </StyledWrapper>
                            );
                        }
                        if ((fields.links[index].fields as any).isMenu === true && linkItem.children.length) {
                            return (
                                <StyledWrapper
                                    className="header_links-popup"
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`link${linkItem.link.url}.${linkItem.link.title}${index}`}
                                >
                                    <Tooltip
                                        trigger="mouseenter focus"
                                        placement="bottom-end"
                                        onOpen={({ instance }: any) => {
                                            setTippyElement(instance);
                                        }}
                                        onClose={({ instance }: any) => {
                                            setTippyElement(undefined);
                                        }}
                                        triggerAltText={translate("Expand Menu")}
                                        triggerComponent={
                                            <StyledWrapper
                                                {...styles.linkWrapper}
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`menu${linkItem.link.url}.${linkItem.link.title}${index}`}
                                                {...({ isSticky, isHomePage, toggleHeaderColor } as any)}
                                                className={isSticky || !isHomePage || toggleHeaderColor ? "toggle" : ""}
                                            >
                                                <Link
                                                    href={!linkItem.excludeFromNavigation ? linkItem.link.url : ""}
                                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                                    arial-role="menu"
                                                    onClick={() => {
                                                        // XNG-Change: track GA4
                                                        trackGaToolbarNavClickEvent(
                                                            fields.links[index].fields.overriddenTitle ||
                                                                linkItem.link.title,
                                                            impersonation,
                                                        );
                                                        closeTooltip();
                                                    }}
                                                >
                                                    <Icon
                                                        src={image}
                                                        className={
                                                            isSticky || !isHomePage || toggleHeaderColor
                                                                ? "link-icon prev toggle"
                                                                : "link-icon prev"
                                                        }
                                                    />
                                                    {fields.links[index].fields.overriddenTitle || linkItem.link.title}
                                                    <Icon
                                                        size={23}
                                                        src="ChevronDown"
                                                        className={
                                                            isSticky || !isHomePage || toggleHeaderColor
                                                                ? "link-icon toggle"
                                                                : "link-icon"
                                                        }
                                                        css={css`
                                                            margin-left: 4px;
                                                        `}
                                                        aria-label="expand"
                                                    />
                                                </Link>
                                            </StyledWrapper>
                                        }
                                        delay={[100, 350]}
                                        offset={[0, 8]}
                                        cssOverrides={{
                                            tooltipBody: css``,
                                            tooltipContainer: css`
                                                max-width: initial;
                                                width: auto;
                                                padding: 15px;
                                                box-shadow: 0 2px 2px 0 rgb(0 0 0 / 33%);
                                                border-radius: 0;
                                                min-width: 120px;
                                                min-height: 80px;
                                            `,
                                        }}
                                        passAsChildren={true}
                                    >
                                        {linkItem.children.map(
                                            (
                                                linkItemL2: {
                                                    url: string | undefined;
                                                    overriddenTitle: any;
                                                    title: any;
                                                    excludeFromNavigation: boolean;
                                                },
                                                index: React.Key | undefined,
                                            ) => {
                                                if (linkItemL2.excludeFromNavigation) {
                                                    return <></>;
                                                }
                                                return (
                                                    <StyledWrapper
                                                        // eslint-disable-next-line react/no-array-index-key
                                                        key={`menul2${linkItemL2.url}.${linkItemL2.title}${index}`}
                                                        css={css`
                                                            margin-bottom: 12px;
                                                            &:last-child {
                                                                margin-bottom: 0;
                                                            }
                                                        `}
                                                    >
                                                        <Link
                                                            href={linkItemL2.url}
                                                            color="text.main"
                                                            css={css`
                                                                span {
                                                                    font-size: 13px;
                                                                    font-weight: 500;
                                                                    line-height: 14px;
                                                                    color: ${getColor("text.main")};
                                                                }
                                                                &:hover,
                                                                &:focus {
                                                                    &&& {
                                                                        span {
                                                                            color: ${getColor("primary")};
                                                                        }
                                                                    }
                                                                }
                                                            `}
                                                            onClick={() => {
                                                                // XNG-Change: track GA4
                                                                trackGaToolbarNavClickEvent(
                                                                    linkItemL2.overriddenTitle || linkItemL2.title,
                                                                    impersonation,
                                                                );
                                                                closeTooltip();
                                                            }}
                                                        >
                                                            {linkItemL2.overriddenTitle || linkItemL2.title}
                                                        </Link>
                                                    </StyledWrapper>
                                                );
                                            },
                                        )}
                                    </Tooltip>
                                </StyledWrapper>
                            );
                        }
                    },
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const allIconsDropDown: { value: string; displayName: string }[] = [];

const allIconsDropDownFn = () => {
    Object.keys(iconsObject).forEach(iconKey => {
        allIconsDropDown.push({ value: iconKey, displayName: iconKey });
    });
};

allIconsDropDownFn();

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HeaderLinkList),
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overriddenTitle, title } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overriddenTitle) {
                            return overriddenTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return title ?? value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "icon",
                        displayName: "Icon",
                        editorTemplate: "DropDownField",
                        options: allIconsDropDown,
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "isMenu",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: fields.l2Links,
                        editorTemplate: "ListField",
                        tooltip: "if the link authored is showing a menu, you need to author the links below.",
                        isVisible: item => item.fields.isMenu === true,
                        getDisplay: (item: HasFields, state) => {
                            const { type, value } = item.fields.destination;
                            const { overriddenTitle, title } = item.fields;
                            if (type === "Page") {
                                const link = getPageLinkByNodeId(state, value);
                                return overriddenTitle || link?.title;
                            }
                            if (type === "Category") {
                                if (overriddenTitle) {
                                    return overriddenTitle;
                                }
                                if (value === emptyGuid) {
                                    return "Products";
                                }
                                const categoryState = getCategoryState(state, value);
                                if (!categoryState.value) {
                                    if (!categoryState.isLoading && value) {
                                        return dispatch => {
                                            dispatch(
                                                loadCategory({
                                                    id: value,
                                                }),
                                            );
                                        };
                                    }
                                    return "";
                                }
                                return categoryState.value.shortDescription;
                            }
                            if (type === "Url") {
                                return title ?? value;
                            }
                            return value;
                        },
                        defaultValue: [],
                        fieldDefinitions: [
                            {
                                name: "destination",
                                editorTemplate: "LinkField",
                                defaultValue: {
                                    value: "",
                                    type: "Page",
                                },
                                isRequired: true,
                            },
                            {
                                name: "overriddenTitle",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                        ],
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
